/**
 * CountingOrderDialog — drag-and-drop reorder for products and prep items in a storage area.
 */
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Check } from "@phosphor-icons/react";

export function CountingOrderDialog({
  open, onOpenChange,
  orderItems, orderSearch, setOrderSearch,
  onOrderChange, onDragStart, onDragOver, onDrop, dragItem,
  savingOrder, onSave
}) {
  const filteredItems = orderItems
    .filter(item => !orderSearch || item.name.toLowerCase().includes(orderSearch.toLowerCase()))
    .sort((a, b) => a.order - b.order);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-wide">EDIT COUNTING ORDER</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-hidden flex flex-col">
          <Input
            value={orderSearch}
            onChange={(e) => setOrderSearch(e.target.value)}
            placeholder="Search items..."
            className="h-9"
            data-testid="order-search"
          />
          <p className="text-xs text-muted-foreground">Drag items or type order numbers. Lower numbers appear first.</p>
          <div className="flex-1 overflow-y-auto space-y-1">
            {filteredItems.map((item, idx) => (
              <div
                key={item.item_id}
                draggable
                onDragStart={() => onDragStart(idx)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(idx)}
                className={`flex items-center gap-2 p-2 rounded-lg border cursor-move transition-colors ${
                  dragItem === idx ? 'bg-primary/10 border-primary' : 'bg-muted/30 hover:bg-muted/50'
                }`}
                data-testid={`order-item-${item.item_id}`}
              >
                <Input
                  type="number"
                  value={item.order}
                  onChange={(e) => onOrderChange(item.item_id, e.target.value)}
                  className="w-14 h-8 text-center text-sm tabular-nums"
                  min="0"
                />
                <span className="flex-1 text-sm font-medium truncate">{item.name}</span>
                <Badge variant={item.item_type === "prep" ? "secondary" : "outline"} className="text-[9px] shrink-0">
                  {item.item_type === "prep" ? "PREP" : "PROD"}
                </Badge>
              </div>
            ))}
          </div>
        </div>
        <DialogFooter className="mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSave} disabled={savingOrder} data-testid="save-order-btn">
            <Check size={16} className="mr-2" /> {savingOrder ? "Saving..." : "Save Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
